import { useIsClient } from '#/hooks/useIsClient';
import type {
  DateString,
  DatetimeString,
  IanaTimezone,
  TimeString,
} from '#/types/TypedDateString';
import { IDEALIST_TIME_ZONE } from '#/utils/constants/general/idealistTimeZone';
import { log } from '#/utils/logging';

// =====================
// Internal utils
// =====================

const DATETIME_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function dateInstanceToStringParts(
  date: Date,
  { timezone }: { timezone: IanaTimezone | null },
) {
  // Format as the new timezone
  const newTimezoneFormattedPartsArray = new Intl.DateTimeFormat('en', {
    timeZone: timezone || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const newTimezoneFormattedParts = Object.fromEntries(
    newTimezoneFormattedPartsArray.map(({ type, value }) => [type, value]),
  );

  // Return new ISO date
  return {
    date: `${newTimezoneFormattedParts.year}-${newTimezoneFormattedParts.month}-${newTimezoneFormattedParts.day}` as DateString,
    time: `${newTimezoneFormattedParts.hour}:${newTimezoneFormattedParts.minute}:${newTimezoneFormattedParts.second}` as TimeString,
  };
}

// =====================
// Idealist
// =====================

export function idealistDateAndTimeStrings(datetime: DatetimeString) {
  if (!DATETIME_REGEX.test(datetime))
    log.error(`Invalid datetime format for ${datetime}`);

  return dateInstanceToStringParts(new Date(datetime), {
    timezone: IDEALIST_TIME_ZONE,
  });
}

export function idealistDateString(datetime: DatetimeString) {
  return idealistDateAndTimeStrings(datetime).date;
}

export function idealistTimeString(datetime: DatetimeString) {
  return idealistDateAndTimeStrings(datetime).time;
}

// =====================
// Local
// =====================

function localDateAndTimeStrings(datetime: DatetimeString) {
  if (!DATETIME_REGEX.test(datetime))
    log.error(`Invalid datetime format for ${datetime}`);

  return dateInstanceToStringParts(new Date(datetime), {
    timezone: null,
  });
}

// =====================
// GTM
// =====================

export function gmtDateAndTimeStrings(datetime: DatetimeString) {
  if (!DATETIME_REGEX.test(datetime))
    log.error(`Invalid datetime format for ${datetime}`);

  return dateInstanceToStringParts(new Date(datetime), {
    timezone: 'Etc/UTC',
  });
}

// =====================
// Local
// =====================

/**
 * We can only use localDateString client-side, otherwise it causes hydration errors
 */
export function clientOnlyLocalDateAndTimeString(datetime: DatetimeString) {
  return localDateAndTimeStrings(datetime);
}

function clientOnlyLocalDateString(datetime: DatetimeString) {
  return clientOnlyLocalDateAndTimeString(datetime).date;
}

/**
 * This hook is a utility to help avoid using local date string before hydration by mistake
 */
export function useLocalDateStringFn() {
  const isClient = useIsClient();

  return {
    localDateString: isClient ? clientOnlyLocalDateString : idealistDateString,
  };
}

export function useLocalDateAndStringsFn() {
  const isClient = useIsClient();

  return {
    localDateAndTimeStrings: isClient
      ? clientOnlyLocalDateAndTimeString
      : idealistDateAndTimeStrings,
  };
}

// =====================
// Timezone
// =====================

export function datetimeFromParts({
  date,
  time,
  timezone,
}: {
  date: DateString;
  time: TimeString;
  timezone: IanaTimezone | null;
}) {
  const zuluDate = new Date(date + 'T' + time);
  const timezoneDate = new Date(
    zuluDate.toLocaleString('en', { timeZone: timezone || undefined }),
  );
  const offsetInMs = zuluDate.getTime() - timezoneDate.getTime();

  return new Date(zuluDate.getTime() + offsetInMs);
}

export function offsetDateAndTimeStrings({
  date,
  time,
  fromTimezone,
  toTimezone,
}: {
  date: DateString;
  time: TimeString;
  fromTimezone: IanaTimezone | null;
  toTimezone: IanaTimezone | null;
}) {
  return dateInstanceToStringParts(
    datetimeFromParts({ date, time, timezone: fromTimezone }),
    { timezone: toTimezone },
  );
}

export function offsetDateInstance(
  dateInstance: Date,
  {
    fromTimezone,
    toTimezone,
  }: { fromTimezone: IanaTimezone | null; toTimezone: IanaTimezone | null },
) {
  const { date, time } = dateInstanceToStringParts(dateInstance, {
    timezone: fromTimezone,
  });

  return offsetDateAndTimeStrings({ date, time, fromTimezone, toTimezone });
}
